import React, { useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import { Menu, MenuProps } from "@gw/web-basic-components";
import type { RenderIconInfo } from "rc-menu/lib/interface";
import {
  convertMenuDataToSelectOptions,
  deepSearchPath,
  menuFilterMap,
} from "./utils";
import { App, AppMenuType } from "../../constant";
import { openComponentUrl, REMOTE_TAG } from "../../utils";
import type { MenuBoxProps } from "./dto";

import { ReactComponent as StarExpandIcon } from "../../assets/menu-icon/seeds-pro/icon_arrow_right.svg";
import { ReactComponent as ArrowSvg } from "../../assets/menu-icon/seeds-pro/arrow.svg";

/** 内部菜单项展开图标 */
const InnerMenuItemExpandIcon = (props: RenderIconInfo & { app: string }) => {
  const { app, isOpen } = props;
  // todo 业务render后，移除此hardCode
  const expandIcon = {
    default: {
      icon: <ArrowSvg />,
      rotate: (bool: boolean) => (bool ? undefined : "rotateX(180deg)"),
    },
    "We-Star": {
      icon: <StarExpandIcon />,
      rotate: (bool: boolean) => (bool ? "rotate(90deg)" : undefined),
    },
    "We-Seeds-Pro": {
      icon: <StarExpandIcon />,
      rotate: (bool: boolean) => (bool ? "rotate(90deg)" : undefined),
    },
  };

  const appExpandIcon = expandIcon?.[app] || expandIcon.default;

  return (
    <div
      className="wbc-menu-editable-title-arrow"
      style={{
        transform: appExpandIcon.rotate(isOpen),
      }}
    >
      {appExpandIcon.icon}
    </div>
  );
};
/**
 * 业务菜单
 */
const MenuBox = (props: MenuBoxProps) => {
  // 防止更新菜单时再次添加titles导致子菜单title渲染多次
  const menuRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { pathname } = useLocation();

  const [selectedKeys, setSelectedKeys] = useState<string[]>();
  const [openKeys, setOpenKeys] = useState<string[]>();

  const { data, authList, app, defaultOpenKeys, expandIcon } = props;

  /** 菜单筛选组件 */
  const MenuFilter = useMemo(() => {
    return menuFilterMap[app as App] || (() => null);
  }, [app]);

  const onClick: MenuProps["onClick"] = (e) => {
    const targetUrl = authList.find(
      (m) => e.key === m.componentId
    )?.componentUrl;
    openComponentUrl(targetUrl, history.push);
  };

  /** 处理筛选框选中新菜单 */
  const handleMenuFilterChange = (value: string | undefined) => {
    if (!value) {
      return;
    }
    const path = deepSearchPath(data, value);
    /** 设置ui状态 */
    setOpenKeys(path);
    setSelectedKeys(path);
    /** 只有菜单类型才跳转 */
    const targetUrl = authList
      .filter((m) => m.componentType === AppMenuType.MENU)
      .find((m) => m.componentId === value)?.componentUrl;
    openComponentUrl(targetUrl, history.push);
  };

  // 控制菜单展开和选中
  useEffect(() => {
    console.log("控制菜单展开和选中");
    if (!authList?.length) return;

    // 找到当前页面的元素
    const currentLeaf = authList
      .filter((m) => m.componentType === 0)
      .find((m) => matchPath(pathname, m.componentUrl)?.isExact);

    function openParent(parentId: string) {
      if (props.initAllExpand) return;
      if (defaultOpenKeys?.length) return;

      const parent = authList.find((m) => m.id === parentId);

      setOpenKeys((val) => {
        return Array.from(new Set([...val, parent?.componentId]));
      });

      // 如果含有父节点，则继续打开
      if (parent.parentId) {
        openParent(parent.parentId);
      }
    }

    // 如果当前页面没有parentId则直接设置open与selected
    if (!currentLeaf?.parentId) {
      if (!props.initAllExpand && !defaultOpenKeys?.length) {
        setOpenKeys([currentLeaf?.componentId]);
      } else if (!openKeys.includes(currentLeaf?.componentId)) {
        setOpenKeys((val) => {
          return [...val, currentLeaf?.componentId];
        });
      }
    } else {
      // 否则找到其parentId对应的组件
      openParent(currentLeaf?.parentId);
    }

    setSelectedKeys([currentLeaf?.componentId]);
  }, [authList, data, pathname, defaultOpenKeys]);

  // 菜单构建key变更时执行
  useEffect(() => {
    if (props.initAllExpand) {
      const expandKeysAll = (data || []).map((m) => m.key);
      setOpenKeys(expandKeysAll);
    }
  }, [data]);

  // 初始化时执行
  useEffect(() => {
    setOpenKeys([...defaultOpenKeys]);
  }, []);

  return (
    <div
      data-app-theme={props.themeCode || props.app}
      data-collapsed={props.collapsed}
      ref={menuRef}
      className={classNames("wbuc-menu-box", props.className)}
    >
      <MenuFilter
        options={convertMenuDataToSelectOptions(data)}
        onSelect={handleMenuFilterChange}
        collapse={props.collapsed}
      />
      <Menu
        onClick={onClick}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={data}
        inlineCollapsed={props.collapsed}
        forceSubMenuRender
        expandIcon={(e) =>
          expandIcon || <InnerMenuItemExpandIcon app={app} {...e} />
        }
        getPopupContainer={() => menuRef.current}
      />
    </div>
  );
};

export default MenuBox;
